/**
 * Показ раздела «тир и возврат тейкеру».
 *
 * Здесь живёт ВСЯ логика, решающая, какое число и с какой подписью видит
 * оператор: vitest берёт только `src/**\/*.test.ts`, компонентных тестов в
 * проекте нет, и правило, вынесенное в JSX, осталось бы непроверенным.
 *
 * Само число приходит с сервера посчитанным (internal/rebates): дублировать
 * формулу тира здесь значило бы завести второе место, где её можно
 * рассинхронизировать, — ровно та ошибка, от которой стережёт lib/fees.ts,
 * зеркалящий комиссию по эталону из testdata.
 */
import type { Rebates } from "../api/types";
import { SCALE, parse } from "./money";
import { formatDollars } from "./tape";

/**
 * Счётные сутки по-русски.
 *
 * «Сутки» — существительное только множественного числа, и «1 суток» читается
 * как опечатка. Мелочь, но она стоит в предупреждении, а предупреждению,
 * написанному с ошибкой, верят меньше.
 */
function days(n: number): string {
  return n === 1 ? "1 сутки" : `${n} суток`;
}

/** Тон строки: тем же словарём, что у сигналов и замка. */
export type Tone = "good" | "warn" | "bad" | "flat";

/** Строка вердикта: короткий заголовок и объяснение под ним. */
export interface Verdict {
  tone: Tone;
  headline: string;
  detail: string;
}

/** Ставка ступени в процентах: сервер шлёт базисные пункты. */
export function ratePercent(bps: number): string {
  return `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 2)} %`;
}

/**
 * Заполненность нынешней ступени: доля пути до следующей, [0, 1].
 *
 * Считается от ПОРОГА нынешней ступени, а не от нуля: полоса, растущая от нуля
 * до миллиона, у Gold стояла бы почти пустой всё время, хотя ступень взята и
 * запас над ней есть. Ноль возвращается там, где следующей ступени нет —
 * полосе тогда нечего показывать.
 */
export function progress(r: Rebates): number {
  if (!r.next) return 1;
  const from = parse(r.tier.threshold_usd) ?? 0;
  const to = parse(r.next.threshold_usd) ?? 0;
  const now = parse(r.weighted_usd) ?? 0;
  if (to <= from) return 0;
  const share = (now - from) / (to - from);
  if (share <= 0) return 0;
  return share >= 1 ? 1 : share;
}

/**
 * Удержится ли нынешняя ступень.
 *
 * Первый вопрос раздела, а не второй. Порог окна — требование НЕПРЕРЫВНОЕ:
 * объём вытекает из скользящих тридцати суток так же, как втекает, и ступень
 * теряют молча — бейдж на профиле просто меняется однажды ночью.
 */
export function holdVerdict(r: Rebates): Verdict {
  if (!r.known) {
    return { tone: "flat", headline: "нечем считать", detail: r.reason ?? "объём не посчитан" };
  }
  if (r.tier.level === 0) {
    return {
      tone: "flat",
      headline: "ступени пока нет",
      detail: `удерживать нечего: до первой ступени нужен объём за ${r.window_days} суток`,
    };
  }

  const need = formatDollars(r.hold.daily_fee_usd);
  const pace = formatDollars(r.pace_usd);
  if (!r.hold.safe) {
    return {
      tone: "bad",
      headline: `${r.tier.name} уходит`,
      detail: `нужно ${need} комиссии в сутки, а темп ${pace} — ступень провалится сама собой`,
    };
  }
  return {
    tone: "good",
    headline: `${r.tier.name} держится`,
    detail: `нужно ${need} комиссии в сутки, темп ${pace}`,
  };
}

/**
 * Стоит ли докатывать до следующей ступени.
 *
 * Главный ответ раздела, и он НЕ «сколько осталось». Порог окна непрерывен,
 * поэтому взять ступень — значит поднять темп навсегда, а не разово дожать
 * разрыв. Сравнивать надо возврат на требуемом темпе с лишней комиссией,
 * которой этот темп обходится, — и разница почти всегда отрицательна, потому
 * что ступень УДЕШЕВЛЯЕТ комиссию, а не платит за объём.
 */
export function climbVerdict(r: Rebates): Verdict {
  if (!r.known) {
    return { tone: "flat", headline: "нечем считать", detail: r.reason ?? "объём не посчитан" };
  }
  if (!r.next) {
    return {
      tone: "good",
      headline: "выше некуда",
      detail: "это высшая ступень программы",
    };
  }

  const bonus = formatDollars(r.next.bonus_usd);
  const rate = `${ratePercent(r.tier.rebate_bps)} → ${ratePercent(r.next.rebate_bps)}`;

  if (r.next.reachable) {
    return {
      tone: "good",
      headline: `${r.next.name} возьмётся сам`,
      detail: `нынешнего темпа хватает: возврат ${rate}, разово ${bonus}`,
    };
  }

  const need = formatDollars(r.next.daily_fee_usd);
  const extra = parse(r.next.extra_fee_usd) ?? 0;
  const gain = parse(r.next.gain_at_required_usd) ?? 0;

  if (extra > 0 && gain > extra) {
    return {
      tone: "good",
      headline: `${r.next.name} окупается`,
      detail:
        `надо держать ${need} комиссии в сутки: возврат ` +
        `${formatDollars(r.next.gain_at_required_usd)} против ` +
        `${formatDollars(r.next.extra_fee_usd)} лишней комиссии, плюс разово ${bonus}`,
    };
  }
  return {
    tone: "warn",
    headline: `${r.next.name} сам по себе не окупается`,
    detail:
      `надо держать ${need} комиссии в сутки — это ` +
      `${formatDollars(r.next.extra_fee_usd)} лишней комиссии против ` +
      `${formatDollars(r.next.gain_at_required_usd)} возврата. ` +
      `Ступень удешевляет комиссию, а не платит за объём: докатывать стоит, ` +
      `только если сама эта торговля прибыльна. Разово ${bonus}`,
  };
}

/**
 * Оговорки: чего расчёт не знает.
 *
 * Стоят на экране, а не в логе. Число, у которого не видно границ
 * применимости, читается как точное — и заниженный объём молча превратился бы
 * в «до ступени далеко».
 */
export function warnings(r: Rebates): string[] {
  const out: string[] = [];
  if (!r.known) return out;

  if (r.gaps.missing_days > 0) {
    out.push(
      `${days(r.gaps.missing_days)} окна ещё не обойдены — объём занижен, ` +
        `тир НЕ НИЖЕ показанного`,
    );
  }
  if (r.gaps.unknown_days > 0) {
    out.push(
      `${days(r.gaps.unknown_days)} с незнакомым расписанием рынка: их объём не посчитан, ` +
        `а не равен нулю`,
    );
  }
  if (r.gaps.partial_days > 0) {
    out.push(
      `${days(r.gaps.partial_days)} обход дочитал не до конца (потолок offset у источника)`,
    );
  }
  if (r.next && r.gaps.bonuses_are_guesses) {
    out.push(
      `разовый бонус ${formatDollars(r.next.bonus_usd)} даётся за ПЕРВОЕ взятие ступени ` +
        `за всю жизнь кошелька, а видно ${r.window_days} суток — если ступень уже брали, ` +
        `бонуса не будет`,
    );
  }
  return out;
}

/** Строка ряда: сутки с прочерками там, где числа нет. */
export interface DayRow {
  key: string;
  day: string;
  trades: string;
  fee: string;
  weighted: string;
  /** Подпись состояния суток; пусто — сутки обычные. */
  note: string;
  tone: Tone;
}

/**
 * Ряд по суткам, СВЕЖИЕ СВЕРХУ.
 *
 * Свежие сверху, потому что первый вопрос к таблице — «что было вчера», а не
 * «что было месяц назад»; месяц назад читают, прокручивая.
 */
export function dayRows(r: Rebates): DayRow[] {
  return [...r.days]
    .sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0))
    .map((d) => {
      const day = d.day.slice(0, 10);
      if (!d.walked) {
        return {
          key: day,
          day,
          trades: "—",
          fee: "—",
          weighted: "—",
          note: "не обойдено",
          tone: "flat" as Tone,
        };
      }
      // У суток бывает НЕСКОЛЬКО расписаний, и незнакомым может оказаться
      // только одно из них. Тогда известная часть объёма — настоящее число, она
      // же и складывается в окно; прятать её за прочерком значило бы показать в
      // таблице не то, что посчитано в итоге.
      const known = parse(d.weighted_usd) ?? 0;
      const weighted = d.known || known > 0 ? formatDollars(d.weighted_usd) : "—";
      const note = !d.known
        ? known > 0
          ? "часть без веса"
          : "вес неизвестен"
        : d.partial
          ? "дочитано не до конца"
          : "";
      return {
        key: day,
        day,
        trades: String(d.trades),
        fee: formatDollars(d.fee_usd),
        weighted,
        note,
        tone: !d.known || d.partial ? ("warn" as Tone) : ("flat" as Tone),
      };
    });
}

/** Строка блока выплат. */
export interface PaidRow {
  label: string;
  value: string;
  hint: string;
}

/**
 * Фактически пришедшие выплаты.
 *
 * Возврат тейкеру, возврат мейкеру и награды показываются ВРОЗЬ: они описывают
 * противоположные способы торговать, и слитые в одну сумму не дают ответить на
 * вопрос, на чём мы живём (ADR-0011 «Решение 1»).
 */
export function paidRows(r: Rebates): PaidRow[] {
  if (!r.paid.known) return [];
  return [
    {
      label: "возврат тейкеру",
      value: formatDollars(r.paid.taker_rebate_usd),
      hint: "то, что даёт эта программа",
    },
    {
      label: "возврат мейкеру",
      value: formatDollars(r.paid.maker_rebate_usd),
      hint: "другая программа: платят за пассивную заявку",
    },
    { label: "награды", value: formatDollars(r.paid.reward_usd), hint: "розыгрыши и акции биржи" },
    { label: "итого", value: formatDollars(r.paid.total_usd), hint: `переводов: ${r.paid.payouts}` },
  ];
}

/**
 * Сверка модели с деньгами: легла ли фактическая ставка на ступень.
 *
 * Не украшение, а приёмка на экране. Возврат обязан быть ровно долей уплаченной
 * комиссии; разъедься подразумеваемая ставка со ступенью — и тир, который мы
 * показываем, посчитан не тем весом или не по всем сделкам.
 */
export function modelCheck(r: Rebates): Verdict | null {
  if (!r.known || !r.paid.known || !r.paid.expected_known) return null;

  const expected = parse(r.paid.expected_usd) ?? 0;
  const actual = parse(r.paid.taker_rebate_usd) ?? 0;
  if (expected <= 0) return null;

  const gap = actual - expected;
  const tolerance = Math.max(expected * 0.03, SCALE); // 3 % или доллар

  if (Math.abs(gap) <= tolerance) {
    return {
      tone: "good",
      headline: `факт сходится: пришло ${formatDollars(r.paid.taker_rebate_usd)}`,
      detail:
        `модель обещала ${formatDollars(r.paid.expected_usd)} за ` +
        `${r.paid.expected_days} суток — сошлось`,
    };
  }
  if (gap > 0) {
    return {
      tone: "flat",
      headline: `пришло больше обещанного: ${formatDollars(r.paid.taker_rebate_usd)}`,
      detail:
        `модель обещала ${formatDollars(r.paid.expected_usd)}. Разница законна, если в ` +
        `переводы попал разовый бонус за взятие ступени — в ожидание он не входит, ` +
        `потому что даётся за первое взятие за всю жизнь кошелька. Иначе биржа ` +
        `насчитала комиссии больше, чем видим мы: часть объёма прошла мимо обхода`,
    };
  }
  return {
    tone: "bad",
    headline: `пришло меньше обещанного: ${formatDollars(r.paid.taker_rebate_usd)}`,
    detail:
      `модель обещала ${formatDollars(r.paid.expected_usd)} за ${r.paid.expected_days} суток. ` +
      `Тир завышен: объём посчитан по чужому весу либо ступень взята позже, чем ` +
      `показывает наш расчёт`,
  };
}

/**
 * Цвет тона — теми же токенами, что везде в интерфейсе.
 *
 * Отдельные имена «buy/sell» тут не годятся по смыслу (ступень не покупают),
 * но цвета те же самые: зелёное значит «хорошо» на всех экранах, и заводить
 * ради раздела свою пару значило бы объяснять оператору, почему здесь другой
 * зелёный.
 */
export function toneColor(tone: Tone): string {
  switch (tone) {
    case "good":
      return "var(--color-buy)";
    case "bad":
      return "var(--color-sell)";
    case "warn":
      return "var(--color-warn)";
    default:
      return "var(--color-muted)";
  }
}
